import { css } from '@emotion/react';
import { Link as RouterLink } from 'react-router-dom';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import EditableNameCell from './EditableNameCell';
import RunStateLabel from './RunStateLabel';
import UserFullName from 'components/UserFullName/UserFullName';

import useUsers from 'store/useUsers';

import IRunEntry from 'types/IRunEntry';
import IColumnDefinition from '../Table/IColumnDefinition';

import { format } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

function formatDate(date: string) {
  return format(new Date(date), 'y MMM d, HH:mm', { locale: enUS });
}

export function useColumns(): IColumnDefinition<IRunEntry>[] {
  const users = useUsers();
  const theme = useTheme();

  return [
    {
      header: 'Name',
      key: 'name',
      render: (run) => <EditableNameCell run={run} />,
      getSearchableValue: (run) => run.name,
      orderProperty: {
        key: 'name',
        type: 'string',
        getVal: (run) => run.name,
      },
    },
    {
      header: 'Date',
      key: 'updated_utc',
      render: (run) => formatDate(run.updated_utc),
      getSearchableValue: (run) => formatDate(run.updated_utc),
      orderProperty: {
        key: 'updated_utc',
        type: 'number',
        getVal: (run) => new Date(run.updated_utc).valueOf(),
      },
    },
    {
      header: 'Id',
      key: 'id',
      getSearchableValue: (run) => run.id,
    },
    {
      header: 'User',
      key: 'user_id',
      render: (run) => <UserFullName id={run.user_id} />,
      getSearchableValue: (run: IRunEntry) => {
        const user = users?.find((user) => user.id === run.user_id);
        return user ? `${user.given_name} ${user.family_name}` : undefined;
      },
    },
    {
      header: 'State',
      key: 'state',
      getSearchableValue: (run) => run.state,
      render: (run) => (
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 6px;
          `}
        >
          <RunStateLabel state={run.state} />
          {run.has_worker_lost_tasks && (
            <Tooltip title="This workflow has tasks whose worker was lost">
              <span
                css={css`
                  color: ${theme.palette.warning.main};
                  font-size: 0.75rem;
                  font-weight: 600;
                `}
              >
                WORKER LOST
              </span>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      header: '',
      // @ts-expect-error – Never mind that there is no "details" property on IRunEntry
      key: 'details',
      render: (run) => (
        <MuiLink component={RouterLink} to={`/projects/${run.project_id}/workflows/${run.id}/details`}>
          Details
        </MuiLink>
      ),
    },
  ];
}

interface IColumnDefinitionWithSearchableValue extends IColumnDefinition<IRunEntry> {
  getSearchableValue: (run: IRunEntry) => string | number | null | undefined;
}

export function useSearchableColumns() {
  return useColumns().filter((column) => !!column.getSearchableValue) as IColumnDefinitionWithSearchableValue[];
}
